import React, { useEffect, useState } from "react";
import Layout, { Container } from "../components/layout";
import axios from "axios";
import { useParams } from "react-router-dom";
import { apiurl } from "../config/config";

const TeamDetails = () => {
  const { userObjId } = useParams();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const res = await axios.post(`${apiurl}/api/user/get/${userObjId}`);
        if (res.data.success) {
          setUser(res.data.data);
        } else {
          setError("User not found.");
        }
      } catch (err) {
        setError("Something went wrong.");
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchUser();
  }, [userObjId]);

  if (loading) return <div className="p-4">Loading...</div>;
  if (error) return <div className="p-4 text-red-600">{error}</div>;
  return (
    <Layout>
      <Container overflow={"overflow-scroll"}>
        <div className="w-full mx-auto p-6 border bg-white rounded-xl shadow-md space-y-6">
          {/* Header */}
          <div className="flex items-center space-x-4">
            {user?.media?.profile?.link ? (
              <img
                src={
                  user?.media?.profile?.link ||
                  "https://via.placeholder.com/100"
                }
                alt="Profile"
                className="w-24 h-24 rounded-full border"
              />
            ) : (
              <div className="w-24 h-24 flex justify-center items-center bg-gray-200 rounded-full border">
                <p className="text-3xl font-bold text-gray-600">
                  {user?.firstName.charAt(0)} {user?.lastName.charAt(0)}
                </p>
              </div>
            )}

            <div>
              <h2 className="text-2xl font-bold capitalize">
                {user?.firstName} {user?.lastName}
              </h2>
              <p className="text-gray-600">{user?.email}</p>
              <p className="text-gray-500">{user?.phone}</p>
            </div>
          </div>

          {/* Role & Status */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <h3 className="font-semibold">User Role</h3>
              <p>{user?.role}</p>
            </div>
            <div>
              <h3 className="font-semibold">Account Status</h3>
              <p
                className={
                  user?.status === "active" ? "text-green-600" : "text-red-600"
                }
              >
                {user?.status}
              </p>
            </div>
          </div>

          {/* Employee Details */}
          <div>
            <h3 className="text-lg font-bold mb-2">Employee Details</h3>
            <div className="grid grid-cols-2 gap-4">
              <p>
                <span className="font-medium">ID:</span>{" "}
                {user?.employeeDetails?.employeeId}
              </p>
              <p>
                <span className="font-medium">Designation:</span>{" "}
                {user?.employeeDetails?.designation}
              </p>
              <p>
                <span className="font-medium">Department:</span>{" "}
                {user?.employeeDetails?.department}
              </p>
              <p>
                <span className="font-medium">Employment Type:</span>{" "}
                {user?.employeeDetails?.employmentType}
              </p>
              <p>
                <span className="font-medium">Joining Date:</span>{" "}
                {new Date(
                  user?.employeeDetails?.joiningDate
                ).toLocaleDateString()}
              </p>
            </div>
          </div>

          {/* Address */}
          <div>
            <h3 className="text-lg font-bold mb-2">Address</h3>
            <div className="grid grid-cols-2 gap-4">
              <p>
                <span className="font-medium">Name:</span> {user?.address?.name}
              </p>
              <p>
                <span className="font-medium">Phone:</span>{" "}
                {user?.address?.phone}
              </p>
              <p>
                <span className="font-medium">Line 1:</span>{" "}
                {user?.address?.line1}
              </p>
              <p>
                <span className="font-medium">Line 2:</span>{" "}
                {user?.address?.line2}
              </p>
              <p>
                <span className="font-medium">City:</span> {user?.address?.city}
              </p>
              <p>
                <span className="font-medium">Province:</span>{" "}
                {user?.address?.province}
              </p>
              <p>
                <span className="font-medium">Zip:</span> {user?.address?.zip}
              </p>
              <p>
                <span className="font-medium">Country:</span>{" "}
                {user?.address?.country}
              </p>
            </div>
          </div>

          {/* Verification */}
          <div>
            <h3 className="text-lg font-bold mb-2">Verification Status</h3>
            <p>
              Email Verified:{" "}
              {user?.verificationDetails?.isEmailVerified ? "✅" : "❌"}
            </p>
            <p>
              Phone Verified:{" "}
              {user?.verificationDetails?.isPhoneVerified ? "✅" : "❌"}
            </p>
          </div>

          {/* Permissions */}
          <div>
            <h3 className="text-lg font-bold mb-2">Permissions</h3>
            <div className="space-y-2">
              {user?.permissions?.map((perm, index) => (
                <div key={index} className="p-2 bg-gray-100 rounded">
                  <p className="font-semibold">{perm.resource}</p>
                  <div className="flex space-x-4 text-sm">
                    {perm.read && <span className="text-green-600">Read</span>}
                    {perm.write && <span className="text-blue-600">Write</span>}
                    {perm.update && (
                      <span className="text-yellow-600">Update</span>
                    )}
                    {perm.delete && (
                      <span className="text-red-600">Delete</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Preferences */}
          <div>
            <h3 className="text-lg font-bold mb-2">Preferences</h3>
            <p>Language: {user?.preferences?.language}</p>
            <p>Theme: {user?.preferences?.theme}</p>
            <div>
              <p className="font-semibold mt-2">Notifications:</p>
              <ul className="list-disc ml-6">
                {user?.preferences?.notifications?.email && <li>Email</li>}
                {user?.preferences?.notifications?.sms && <li>SMS</li>}
                {user?.preferences?.notifications?.push && <li>Push</li>}
              </ul>
            </div>
          </div>

          {/* Timestamps */}
          <div className="text-sm text-gray-500">
            <p>Created At: {new Date(user?.createdAt).toLocaleString()}</p>
            <p>Last Updated: {new Date(user?.updatedAt).toLocaleString()}</p>
            <p>
              Last Login:{" "}
              {user?.lastLogin
                ? new Date(user?.lastLogin).toLocaleString()
                : "N/A"}
            </p>
          </div>
        </div>
      </Container>
    </Layout>
  );
};

export default TeamDetails;